import { Component, Input, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { GlobalApp } from "../../tools/global";
import { PreFilterService } from "../../api/services";
import { PreFilterValue, PreFilterValueType } from "../../api/models";
import { TreeEditorNode, TreeEditorComponent } from "../tree-editor/tree-editor.component";
import { CUSTOM_ELEMENTS_SCHEMA } from "@angular/compiler";
import { PreFilterValuesService } from "../../services/pre-filter-values-service";

@Component({
  selector: 'pre-filter-selection',
  templateUrl: './pre-filter-selection.component.html',
  styleUrls: ['./pre-filter-selection.component.css']
})
export class PreFilterSelectionComponent implements OnInit, OnDestroy {

  @ViewChild('titlesTree') titlesTree: TreeEditorComponent;
  @ViewChild('occupationsTree') occupationsTree: TreeEditorComponent;
  @ViewChild('regionsTree') regionsTree: TreeEditorComponent;
  @ViewChild('industriesTree') industriesTree: TreeEditorComponent;
  @ViewChild('organizationsTree') organizationsTree: TreeEditorComponent;   

  @Input() preFilterId: number = 0;
  loadingPreFilter: boolean = false;
  savingPreFilter: boolean = false;

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  titleSearch: string[] = [];
  titleIsAnd: boolean = false;
  loadingTitles: boolean = true;
  selectedTitles: TreeEditorNode[] = [];
  availableTitles: TreeEditorNode[] = [];

  organizationSearch: string[] = [];
  organizationIsAnd: boolean = false;
  loadingOrganizations: boolean = true;
  selectedOrganizations: TreeEditorNode[] = [];
  availableOrganizations: TreeEditorNode[] = [];

  loadingOccupations: boolean = true;
  selectedOccupations: TreeEditorNode[] = [];
  availableOccupations: TreeEditorNode[] = [];

  loadingRegions: boolean = true;
  selectedRegions: TreeEditorNode[] = [];
  availableRegions: TreeEditorNode[] = [];

  loadingIndustries: boolean = true;
  selectedIndustries: TreeEditorNode[] = [];
  availableIndustries: TreeEditorNode[] = [];

  constructor(
    private preFilterService: PreFilterService,
    private preFilterValueService: PreFilterValuesService,
    private app: GlobalApp,
  ) {
  }

  ngOnInit(): void {

  }

  ngOnDestroy(): void {
  }

  valuesLoading(): boolean {
    return !this.preFilterValueService.allDataLoaded();
  }

  loadCoreValues(): Promise<void> {
    return new Promise<void>((success, error) => {
      if (!this.preFilterValueService.allDataLoaded()) {
        const sub = this.preFilterValueService.allDataLoadedChange.subscribe((value) => {
          if (value) {
            Promise.all([
              this.loadTitles(),  
              this.loadOrganizations(),
              this.loadOccupations(),
              this.loadIndustries(),
              this.loadRegions()
            ]).then(_ => {
              sub.unsubscribe();
              success(null);
            });
          }
        });
      } else {
        Promise.all([
          this.loadTitles(),
          this.loadOrganizations(),
          this.loadOccupations(),
          this.loadIndustries(),
          this.loadRegions()
        ]).then(_ => {
          success(null);
        });
      }
    });
  }

  public loadPreFilterValues(): void {
    var allPromises = [];

    if (this.titleSearch.length > 0 || this.titleIsAnd) {
      this.titleSearch = [];
      this.titleIsAnd = false;
      allPromises.push(this.loadTitles());
    }

    if (this.organizationSearch.length > 0 || this.organizationIsAnd) {
      this.organizationSearch = [];
      this.organizationIsAnd = false;
      allPromises.push(this.loadOrganizations());
    }

    if (allPromises.length > 0) {
      Promise.all(allPromises).then(_ => {
        this.loadActualPreFilterValues();
      });
    } else {
      this.loadActualPreFilterValues();
    }
  }

  private loadActualPreFilterValues() {
    this.loadingPreFilter = true;
    this.preFilterService.preFilterGetPreFilterById(this.preFilterId).subscribe(async preFilterValueResults => {

      this.titleSearch = [];
      this.organizationSearch = [];

      this.titleIsAnd = preFilterValueResults.titlesFuzzyAnd;
      this.organizationIsAnd = preFilterValueResults.companiesFuzzyAnd;

      preFilterValueResults.preFilterValues.filter(x => x.valueType === 11).forEach(x => this.titleSearch.push(x.value));
      preFilterValueResults.preFilterValues.filter(x => x.valueType === 14).forEach(x => this.organizationSearch.push(x.value));
      await this.loadTitles();
      await this.loadOrganizations();

      preFilterValueResults.preFilterValues.forEach(value => {
        var selValue: TreeEditorNode | undefined = undefined;

        if (value.valueType === 0) {
          selValue = this.getSelectedTitleValue(value);
          if (!selValue) {
            this.preFilterValueService.getTitleValueById(value.value).then((hiddenTitleValue) => {
              if (hiddenTitleValue) {
                selValue = {
                  parent: undefined,
                  children: [],
                  item: hiddenTitleValue.title,
                  value: hiddenTitleValue.titleId.toString(),
                  source: hiddenTitleValue,
                  sourceType: 0
                }
                this.availableTitles = [selValue, ...this.availableTitles];
              }
            });
          }
          if (selValue) this.selectedTitles.push(selValue);
        } else if (value.valueType === 13) {
          selValue = this.getSelectedOrganizationValues(value);
          if (!selValue) {
            this.preFilterValueService.getOrganizationValueById(value.value).then((hiddenOrganizationValue) => {
              if (hiddenOrganizationValue) {
                selValue = {
                  parent: undefined,
                  children: [],
                  item: hiddenOrganizationValue.organizationName,
                  value: hiddenOrganizationValue.organizationId,
                  source: hiddenOrganizationValue,
                  sourceType: 1
                }
                this.availableOrganizations = [selValue, ...this.availableOrganizations];
              }
            });
          }
          if (selValue) this.selectedOrganizations.push(selValue);
        } else if (value.valueType === 2 || value.valueType === 3) {
          selValue = this.getSelectedRegionValue(value);
          if (selValue) this.selectedRegions.push(selValue);
        } else if (value.valueType === 4 || value.valueType === 5) {
          selValue = this.getSelectedIndustryValue(value);
          if (selValue) this.selectedIndustries.push(selValue);
        } else if (value.valueType === 6 ||
          value.valueType === 7 ||
          value.valueType === 8 ||
          value.valueType === 9 ||
          value.valueType === 10) {
          selValue = this.getSelectedOccupationValue(value);
          if (selValue) this.selectedOccupations.push(selValue);
        } 
      });

      if (this.titleSearch.length > 0) {
        await this.loadTitles();
      }

      if (this.organizationSearch.length > 0) {
        await this.loadOrganizations();
      }

      this.titlesTree.setSelectedValues(this.selectedTitles);
      this.organizationsTree.setSelectedValues(this.selectedOrganizations);
      this.regionsTree.setSelectedValues(this.selectedRegions);
      this.industriesTree.setSelectedValues(this.selectedIndustries);
      this.occupationsTree.setSelectedValues(this.selectedOccupations);

      

      this.loadingPreFilter = false;
    });
  }

  public savePreFilterValues(): Promise<void> {
    return new Promise<void>((success, error) => {
      if (this.preFilterId !== 0) {
        this.savingPreFilter = true;
        var allPromises = [];
        allPromises.push(this.preFilterService.preFilterSetPreFilterTitles({
          preFilterId: this.preFilterId,
          titles: this.getPreFilterTitleValues()
        }).toPromise());
        allPromises.push(this.preFilterService.preFilterSetPreFilterOrganizations({
          preFilterId: this.preFilterId,
          organizations: this.getPreFilterOrganizationValues()
        }).toPromise())
        allPromises.push(this.preFilterService.preFilterSetPreFilterRegions({
          preFilterId: this.preFilterId,
          stateIds: this.getPreFilterRegionStateValues(),
          cbsas: this.getPreFilterRegionMetroAreaValues()
        }).toPromise());
        allPromises.push(this.preFilterService.preFilterSetPreFilterIndustries({
          preFilterId: this.preFilterId,
          sectorIds: this.getPreFilterIndustrySectorValues(),
          industryIds: this.getPreFilterIndustryIndustryValues()
        }).toPromise());
        allPromises.push(this.preFilterService.preFilterSetPreFilterOccupations({
          preFilterId: this.preFilterId,
          majorGroupIds: this.getPreFilterOccupationMajorGroupValues(),
          minorGroupIds: this.getPreFilterOccupationMinorGroupValues(),
          broadGroupIds: this.getPreFilterOccupationBroadGroupValues(),
          detailsIds: this.getPreFilterOccupationDetailValues(),
          socCodes: this.getPreFilterOccupationSocValues()
        }).toPromise());
        allPromises.push(this.preFilterService.preFilterSetPreFilterFuzzyTitles({
          preFilterId: this.preFilterId,
          titles: this.titleSearch
        }).toPromise());
        allPromises.push(this.preFilterService.preFilterSetPreFilterFuzzyOrganizations({
          preFilterId: this.preFilterId,
          organizations: this.organizationSearch
        }).toPromise());
        allPromises.push(this.preFilterService.preFilterSetPreFilterFuzzyAndOr({
          preFilterId: this.preFilterId,
          titlesAnd: this.titleIsAnd,
          companiesAnd: this.organizationIsAnd
        }).toPromise());

        Promise.all(allPromises).then(res => {
          this.savingPreFilter = false;
          success(null);
        });
      }
    });
  }

  async loadTitles(): Promise<void> {
    this.loadingTitles = true;

    this.availableTitles = (await this.preFilterValueService.getTitleValues(this.titleSearch, this.titleIsAnd)).map((x): TreeEditorNode => ({
      parent: undefined,
      children: [],
      item: x.title,
      value: x.titleId.toString(),
      source: x,
      sourceType: 0
    }));

    this.loadingTitles = false;
  }

  addTitleSearch(event: MatChipInputEvent) {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      this.titleSearch.push(value.trim());
      this.loadTitles().then(_ => {});
    }

    event.input!.value = '';
  }

  removeTitleSearch(titleSearch: string): void {
    const index = this.titleSearch.indexOf(titleSearch);

    if (index >= 0) {
      this.titleSearch.splice(index, 1);
      this.loadTitles().then(_ => { });
    }
  }

  toggleTitleAndOr(): void {
    this.titleIsAnd = !this.titleIsAnd;
    this.loadTitles().then(_ => { });
  }

  async loadOrganizations(): Promise<void> {
    this.loadingOrganizations = true;

    this.availableOrganizations = (await this.preFilterValueService.getOrganizationValues(this.organizationSearch, this.organizationIsAnd)) .map((x): TreeEditorNode => ({
      parent: undefined,
      children: [],
      item: x.organizationName,
      value: x.organizationId,
      source: x,
      sourceType: 13
    }));

    this.loadingOrganizations = false;
  }

  addOrganizationSearch(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      this.organizationSearch.push(value.trim());
      this.loadOrganizations().then(_ => { });
    }

    event.input!.value = '';
  }

  removeOrganizationSearch(organizationSearch: string): void {
    const index = this.organizationSearch.indexOf(organizationSearch);

    if (index >= 0) {
      this.organizationSearch.splice(index, 1);
      this.loadOrganizations().then(_ => { });
    }
  }

  toggleOrganizationAndOr(): void {
    this.organizationIsAnd = !this.organizationIsAnd;
    this.loadOrganizations().then(_ => { });
  }

  loadOccupations(): Promise<void> {
    this.loadingOccupations = true;
    return new Promise<void>((success, error) => {
      var tenMajorGroups: TreeEditorNode[] = [];
      this.preFilterValueService.getOccupationValues().forEach(majorGroup => {
        var tenMajorGroup: TreeEditorNode = {
          parent: undefined,
          children: [],
          item: majorGroup.majorGroup,
          value: majorGroup.majorGroupId,
          source: majorGroup,
          sourceType: 6
        };

        majorGroup.minorGroups.forEach(minorGroup => {
          var tenMinorGroup: TreeEditorNode = {
            parent: tenMajorGroup,
            children: [],
            item: minorGroup.minorGroup,
            value: minorGroup.minorGroupId,
            source: minorGroup,
            sourceType: 7
          };

          minorGroup.broadGroups.forEach(broadGroup => {
            var tenBroadGroup: TreeEditorNode = {
              parent: tenMinorGroup,
              children: [],
              item: broadGroup.broadGroup,
              value: broadGroup.broadGroupId,
              source: broadGroup,
              sourceType: 8
            };

            broadGroup.occupationDetails.forEach(occupationDetail => {
              var tenDetail: TreeEditorNode = {
                parent: tenBroadGroup,
                children: [],
                item: occupationDetail.detail,
                value: occupationDetail.detailId,
                source: occupationDetail,
                sourceType: 9
              };

              occupationDetail.occupationOnets.forEach(occupationOnet => {
                tenDetail.children.push({
                  parent: tenDetail,
                  children: [],
                  item: occupationOnet.onetOccupation,
                  value: occupationOnet.onetSocCode,
                  source: occupationOnet,
                  sourceType: 10
                });
              });

              tenBroadGroup.children.push(tenDetail);
            });

            tenMinorGroup.children.push(tenBroadGroup);
          });

          tenMajorGroup.children.push(tenMinorGroup);
        });

        tenMajorGroups.push(tenMajorGroup);
      });
      this.availableOccupations = tenMajorGroups;
      this.loadingOccupations = false;
      success(null);
      /*
      this.loadingOccupations = false;
      success(null);*/
    });
  }

  loadIndustries(): Promise<void> {
    this.loadingIndustries = true;

    return new Promise<void>((success, error) => {
      var tenSectors: TreeEditorNode[] = [];
      this.preFilterValueService.getIndustryValues().forEach(sector => {
        var tenSector: TreeEditorNode = {
          parent: undefined,
          children: [],
          item: sector.industry,
          value: sector.industry,
          source: sector,
          sourceType: 4
        };

        sector.subIndustries.forEach(industry => {
          tenSector.children.push({
            parent: tenSector,
            children: [],
            item: industry.subIndustry,
            value: industry.subIndustry,
            source: industry,
            sourceType: 5
          });
        });

        tenSectors.push(tenSector);
      });
      this.availableIndustries = tenSectors;
      this.loadingIndustries = false;
      success(null);
    })
  }

  loadRegions(): Promise<void> {
    this.loadingRegions = true;

    return new Promise<void>((success, error) => {
      var tenRegionStates: TreeEditorNode[] = [];
      this.preFilterValueService.getRegionValues().forEach(regionState => {
        var tenRegionState: TreeEditorNode = {
          parent: undefined,
          children: [],
          item: regionState.stateName,
          value: regionState.state,
          source: regionState,
          sourceType: 2
        };

        regionState.metroAreas.forEach(metroArea => {

          tenRegionState.children.push({
            parent: tenRegionState,
            children: [],
            item: metroArea.metroArea,
            value: metroArea.cbsaCode,
            source: metroArea,
            sourceType: 3
          });
        });

        tenRegionStates.push(tenRegionState);
      });
      this.availableRegions = tenRegionStates;
      this.loadingRegions = false;
      success(null);
    });
  }

  getSelectedTitleValue(value: PreFilterValue): TreeEditorNode | undefined {
    return this.availableTitles.find(x => x.value === value.value);
  }

  getSelectedOrganizationValues(value: PreFilterValue): TreeEditorNode | undefined {
    return this.availableOrganizations.find(x => x.value === value.value);
  }

  getSelectedOccupationValue(value: PreFilterValue): TreeEditorNode | undefined {
    var selectedValue: TreeEditorNode | undefined = undefined;

    for (var majorGroupId = 0; majorGroupId < this.availableOccupations.length; majorGroupId++) {
      var majorGroup = this.availableOccupations[majorGroupId];

      if (value.valueType === 6) {
        if (majorGroup.value === value.value) {
          selectedValue = majorGroup;
          break;
        }
      } else {
        for (var minorGroupId = 0; minorGroupId < majorGroup.children.length; minorGroupId++) {
          var minorGroup = majorGroup.children[minorGroupId];

          if (value.valueType === 7) {
            if (minorGroup.value === value.value) {
              selectedValue = minorGroup;
              break;
            }
          } else {
            for (var broadGroupId = 0; broadGroupId < minorGroup.children.length; broadGroupId++) {
              var broadGroup = minorGroup.children[broadGroupId];

              if (value.valueType === 8) {
                if (broadGroup.value === value.value) {
                  selectedValue = broadGroup;
                  break;
                }
              } else {
                for (var detailId = 0; detailId < broadGroup.children.length; detailId++) {
                  var detail = broadGroup.children[detailId];
                  if (value.valueType === 9) {
                    if (detail.value === value.value) {
                      selectedValue = detail;
                      break;
                    }
                  } else {
                    for (var socId = 0; socId < detail.children.length; socId++) {
                      var soc = detail.children[socId];

                      if (soc.value === value.value) {
                        selectedValue = soc;
                        break;
                      }
                    }
                  }
                  if (selectedValue) break;
                }
              }
              if (selectedValue) break;
            }
          }
          if (selectedValue) break;
        }
      }
      if (selectedValue) break;
    }

    return selectedValue;
  }

  getSelectedIndustryValue(value: PreFilterValue): TreeEditorNode | undefined {
    var selectedValue: TreeEditorNode | undefined = undefined;

    for (var sectorId = 0; sectorId < this.availableIndustries.length; sectorId++) {
      var sector = this.availableIndustries[sectorId];

      if (value.valueType === 4) {
        if (sector.value === value.value) {
          selectedValue = sector;
          break;
        }
      } else {
        for (var industryId = 0; industryId < sector.children.length; industryId++) {
          var industry = sector.children[industryId];

          if (value.valueType === 5) {
            if (industry.value === value.value) {
              selectedValue = industry;
              break;
            }
          }
          if (selectedValue) break;
        }
      }
      if (selectedValue) break;
    }

    return selectedValue;
  }

  getSelectedRegionValue(value: PreFilterValue): TreeEditorNode | undefined {
    var selectedValue: TreeEditorNode | undefined = undefined;

    for (var stateId = 0; stateId < this.availableRegions.length; stateId++) {
      var state = this.availableRegions[stateId];

      if (value.valueType === 2) {
        if (state.value === value.value) {
          selectedValue = state;
          break;
        }
      } else {
        for (var metroAreaId = 0; metroAreaId < state.children.length; metroAreaId++) {
          var metroArea = state.children[metroAreaId];

          if (metroArea.value === value.value) {
            selectedValue = metroArea;
            break;
          }
        }
      }

      if (selectedValue) break;
    }

    return selectedValue;
  }

  getPreFilterTitleValues(): string[] { return this.titlesTree.getSelectedValues().map(x => x.value); }

  getPreFilterOrganizationValues(): string[] { return this.organizationsTree.getSelectedValues().map(x => x.value); }

  getPreFilterRegionStateValues(): string[] { return this.regionsTree.getSelectedValues().filter(a => a.sourceType === 2).map(x => x.value); }

  getPreFilterRegionMetroAreaValues(): string[] { return this.regionsTree.getSelectedValues().filter(a => a.sourceType === 3).map(x => x.value); }

  getPreFilterIndustrySectorValues(): string[] { return this.industriesTree.getSelectedValues().filter(a => a.sourceType === 4).map(x => x.value); }

  getPreFilterIndustryIndustryValues(): string[] { return this.industriesTree.getSelectedValues().filter(a => a.sourceType === 5).map(x => x.value); }

  getPreFilterOccupationMajorGroupValues(): string[] { return this.occupationsTree.getSelectedValues().filter(a => a.sourceType === 6).map(x => x.value); }

  getPreFilterOccupationMinorGroupValues(): string[] { return this.occupationsTree.getSelectedValues().filter(a => a.sourceType === 7).map(x => x.value); }

  getPreFilterOccupationBroadGroupValues(): string[] { return this.occupationsTree.getSelectedValues().filter(a => a.sourceType === 8).map(x => x.value); }

  getPreFilterOccupationDetailValues(): string[] { return this.occupationsTree.getSelectedValues().filter(a => a.sourceType === 9).map(x => x.value); }

  getPreFilterOccupationSocValues(): string[] { return this.occupationsTree.getSelectedValues().filter(a => a.sourceType === 10).map(x => x.value); }
}
